import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Permission } from '@today/types'
import type { TableSort } from '@today/ui-components'
import { type CustomerFilters, CustomerSortKey } from '@today/ui-domain'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { customerCapabilities } from '@/auth/customer-capabilities'
import { useViewerPermissions } from '@/auth/useViewerPermissions'
import { customerService } from '@/services/customer.service'
import { toRow, unitOptions } from './customer-ui'

const FILTER_KEYS: Array<keyof CustomerFilters> = ['type', 'status', 'orgUnitId', 'tags', 'search']

/** The `order` values of the URL and of `GET /customers` (TO-697 REQ-004). */
enum SortOrder {
	ASC = 'asc',
	DESC = 'desc',
}

const SORT_KEYS: readonly string[] = Object.values(CustomerSortKey)

/** The sort in the URL, or undefined (server default) when `sort` is absent or not a sortable column. */
function sortFromUrl(params: URLSearchParams): TableSort | undefined {
	const key = params.get('sort')
	if (key === null || !SORT_KEYS.includes(key)) return undefined
	// Anything but `desc` reads as ascending, so a mangled `order` still yields a valid request.
	return { key, direction: params.get('order') === SortOrder.DESC ? 'descending' : 'ascending' }
}

const orderOf = (sort: TableSort): SortOrder => (sort.direction === 'descending' ? SortOrder.DESC : SortOrder.ASC)

/** URL updates that remove every filter and the page, leaving the sort (REQ-007 Clear filters). */
const CLEARED_FILTERS: Record<string, string> = Object.fromEntries([...FILTER_KEYS, 'page'].map((key) => [key, '']))

/** The list filters as the URL holds them; an absent parameter is the empty string. */
function filtersFromUrl(params: URLSearchParams): CustomerFilters {
	return {
		type: params.get('type') ?? '',
		status: params.get('status') ?? '',
		orgUnitId: params.get('orgUnitId') ?? '',
		tags: params.get('tags') ?? '',
		search: params.get('search') ?? '',
	}
}

/** `GET /customers` query params: the page, 20 per page, each set filter, and the sort when valid. */
function listRequest(filters: CustomerFilters, page: number, sort: TableSort | undefined): Record<string, string> {
	const request: Record<string, string> = { page: String(page), limit: '20' }
	for (const key of FILTER_KEYS) {
		if (filters[key]) request[`filters[${key}]`] = filters[key]
	}
	if (sort) {
		request.sort = sort.key
		request.order = orderOf(sort)
	}
	return request
}

/** Writes several query parameters at once; an empty value removes its parameter. */
function useUrlUpdate(): [URLSearchParams, (next: Record<string, string>) => void] {
	const [params, setParams] = useSearchParams()
	const update = (next: Record<string, string>) => {
		const merged = new URLSearchParams(params)
		for (const [key, value] of Object.entries(next)) {
			if (value) merged.set(key, value)
			else merged.delete(key)
		}
		setParams(merged)
	}
	return [params, update]
}

/** The navigations the list offers, each present only when the viewer's atoms permit it (REQ-005). */
function useListNavigation() {
	const navigate = useNavigate()
	const { permissions } = useViewerPermissions()
	const mayWriteCustomers = customerCapabilities(permissions).write
	const mayWriteOffers = permissions.includes(Permission.OFFER_WRITE)
	return {
		onOpen: (id: string) => navigate(`/customers/${encodeURIComponent(id)}`),
		onCreate: mayWriteCustomers ? () => navigate('/customers/new') : undefined,
		onStartOffer: mayWriteOffers
			? (id: string) => navigate(`/offers/new?customerId=${encodeURIComponent(id)}`)
			: undefined,
	}
}

/**
 * List page state (TO-418 customer-ui REQ-003, TO-697 customers-workspace-list): filters,
 * page and sort live in the URL query string; a 1-character search sends no request.
 */
export function useCustomerList() {
	const { t, i18n } = useTranslation()
	const [params, update] = useUrlUpdate()
	const filters = filtersFromUrl(params)
	const page = Number(params.get('page') ?? '1')
	const sort = sortFromUrl(params)
	const searchTooShort = filters.search.length === 1
	const request = listRequest(filters, page, sort)

	const list = useQuery({
		queryKey: ['customers', request],
		queryFn: () => customerService.list(request),
		enabled: !searchTooShort,
		placeholderData: keepPreviousData,
	})
	const units = useQuery({ queryKey: ['org-units'], queryFn: customerService.orgUnits })

	return {
		t,
		rows: (list.data?.data ?? []).map((c) => toRow(c, i18n.language)),
		filters,
		orgUnits: unitOptions(units.data ?? []),
		page,
		pageCount: list.data?.totalPages ?? 0,
		total: list.data?.total ?? 0,
		// Pending AND fetching: a query disabled by the 1-character guard is not "loading".
		loading: list.isLoading,
		sort,
		error: list.isError,
		searchTooShort,
		onFilterChange: (key: keyof CustomerFilters, value: string) => update({ [key]: value, page: '' }),
		onPageChange: (next: number) => update({ page: String(next) }),
		onSortChange: (next: TableSort) => update({ sort: next.key, order: orderOf(next), page: '' }),
		onClearFilters: () => update(CLEARED_FILTERS),
		onRetry: () => void list.refetch(),
		...useListNavigation(),
	}
}
